import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { DateAdapter, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { dateWithoutTime } from '../utils/date.utils';

@Component({
  selector: 'input-date',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    TranslateModule
  ],
  templateUrl: './input-date.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputDateComponent),
      multi: true
    },
    { provide: DateAdapter, useClass: NativeDateAdapter },

  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDateComponent implements ControlValueAccessor, OnInit {
  @Input() readOnly = false;
  @Input() label = '';
  @Input() requiredLabel = 'date-required'
  @Input() required = false;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() disabledDates: Date[] = [];


  control = new FormControl<Date | null>(null);

  onChange = (value: Date | null) => { };
  onTouched = () => { };

  ngOnInit(): void {
    if (this.required) {
      this.control.setValidators(Validators.required);
    }

    this.control.valueChanges.subscribe((value) => {
      // set the date (that doesn't care about time) 
      const dateWithOutTime = dateWithoutTime(value)
      this.onChange(dateWithOutTime);
      this.onTouched();
    });
  }

  writeValue(value: Date | null): void {
    if (value) {
      this.control.setValue(value, { emitEvent: false });
    } else {
      this.control.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;

    // Ensure we're comparing without time
    const current = dateWithoutTime(date)!.getTime();

    // Check min / max
    if (this.minDate && current < dateWithoutTime(this.minDate)!.getTime()) {
      return false;
    }
    if (this.maxDate && current > dateWithoutTime(this.maxDate)!.getTime()) {
      return false;
    }

    // Check disabled dates
    const isDisabled = this.disabledDates
      .map(d => dateWithoutTime(d)?.getTime())
      .includes(current);

    return !isDisabled;
  };

}
